import React, { useEffect, useState } from 'react';
import { useNavigate, Navigate } from 'react-router';
import { useAuth } from '../lib/auth';
import { isTablet, isTabletConsent } from '../lib/roles';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { ClinicPilotLogo, ClinicPilotBadge } from '../components/ClinicPilotBrand';
import { toast } from 'sonner';
import { useClinica } from '../lib/clinica';

/** Phase 24 — Tras login decide a dónde mandar al usuario.
 * El kiosk del mostrador (`role==='tablet'`) va siempre a /firma-lopd.
 * El kiosk de consentimientos (`role==='tablet_consent'`) va a /firma-consent.
 * El resto va al dashboard.
 */
function landingPathFor(user) {
  if (isTablet(user)) return '/firma-lopd';
  if (isTabletConsent(user)) return '/firma-consent';
  return '/';
}

// Servidor de demostraciones: acceso de un clic por rol, con la contraseña
// DEMO_PASSWORD que manda el servidor solo si el autologin está activo. Solo el
// rol: el nombre de cada usuario de prueba lo pone la semilla. Solo se
// muestra cuando el backend devuelve `demo_autologin: true` — en una
// instalación real esta bandera es siempre false y todo este bloque no
// aparece ni se ejecuta. Las cuentas las manda el servidor (`demo_accounts`,
// las del equipo de la demo activa: una demo personalizada tiene las suyas);
// esta lista es la de la demo general, por si no llegan.
const DEMO_ACCOUNTS = [
  { label: 'Administrador', email: 'daniel@clinica.test' },
  { label: 'Médico', email: 'elena@clinica.test' },
  { label: 'Enfermería', email: 'irene@clinica.test' },
  { label: 'Administrativo', email: 'ana@clinica.test' },
];

export default function LoginPage() {
  const clinicaDatos = useClinica();
  const cuentasDemo = (clinicaDatos.demo_accounts && clinicaDatos.demo_accounts.length)
    ? clinicaDatos.demo_accounts : DEMO_ACCOUNTS;
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  // Demo: entra sola como Administrador al cargar, salvo que el propio
  // usuario haya cerrado sesión a propósito para probar otro rol
  // (marca `cp_elegir_rol`, ver AppShell).
  const [entrandoSolo, setEntrandoSolo] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!clinicaDatos.demo_autologin || !clinicaDatos.demo_password || user) return;
    if (sessionStorage.getItem('cp_elegir_rol') === '1') return;
    let cancelado = false;
    setEntrandoSolo(true);
    login(cuentasDemo[0].email, clinicaDatos.demo_password).then((res) => {
      if (cancelado) return;
      if (res?.ok) navigate('/');
      else setEntrandoSolo(false);
    }).catch(() => { if (!cancelado) setEntrandoSolo(false); });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicaDatos.demo_autologin]);

  if (user) return <Navigate to={landingPathFor(user)} replace />;

  if (entrandoSolo) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 bg-white">
        <ClinicPilotLogo height={34} />
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Entrando en {clinicaDatos.clinic_name || 'la demo'}…
        </div>
      </div>
    );
  }

  const quickLogin = async (acc) => {
    sessionStorage.removeItem('cp_elegir_rol');
    setBusy(true);
    const res = await login(acc.email, clinicaDatos.demo_password);
    setBusy(false);
    if (res.ok) {
      toast.success(`Entrando como ${acc.label}`);
      navigate(landingPathFor(res.user || user));
    } else {
      toast.error(res.error || 'No se pudo entrar');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success('Sesión iniciada');
      // Esperamos al siguiente tick para que `user` esté disponible en el contexto
      // si necesitamos redirigir según rol; aún así Navigate de arriba lo gestiona
      // cuando el provider actualiza. Aquí solo lanzamos navigate a la landing.
      navigate(landingPathFor(res.user || user));
    } else {
      toast.error(res.error || 'Error');
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative bg-[#2E6E5E] items-center justify-center px-12">
        <div className="absolute inset-0 cp-gradient opacity-90"></div>
        <div className="relative z-10 text-white max-w-md">
          {/* Producto primero: esto es ClinicPilot, configurado para Demo. */}
          <ClinicPilotLogo dark height={52} className="mb-4" />
          <p className="text-white/80 text-sm uppercase tracking-[0.14em] font-semibold mb-10">
            Software de gestión clínica con IA, a medida
          </p>
          <p className="text-white/85 text-lg leading-relaxed">
            Inteligencia artificial que trabaja por ti: automatiza la agenda, los presupuestos, los consentimientos y los cobros — todo en un solo lugar, desde cualquier dispositivo.
          </p>
          <div className="mt-12 border-t border-white/15 pt-6 text-sm text-white/80 leading-snug">
            Creado para <span className="font-semibold text-white">{clinicaDatos.clinic_name}</span>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center px-4 sm:px-12 py-12 bg-white">
        <Card className="w-full max-w-md cp-card border-none shadow-none">
          <CardContent className="p-6 sm:p-10">
            <div className="lg:hidden mb-8">
              <ClinicPilotLogo height={26} />
              <div className="mt-2 text-xs text-muted-foreground">
                para <span className="font-semibold text-[#13231E]">{clinicaDatos.clinic_name}</span>
              </div>
            </div>
            <h2 className="font-display text-3xl font-semibold text-[#13231E]">Iniciar sesión</h2>
            <p className="text-sm text-muted-foreground mt-1 mb-8">Accede con tu nombre o email.</p>
            <form className="space-y-4" onSubmit={handleSubmit}>
              <div>
                <Label htmlFor="email">Usuario o email</Label>
                <Input
                  id="email"
                  type="text"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  inputMode="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Ej. usuario"
                  data-testid="login-email-input"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPwd ? 'text' : 'password'}
                    autoComplete="current-password"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                  <button type="button" onClick={() => setShowPwd(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={busy}
                data-testid="login-submit-button"
                className="w-full bg-[#2E6E5E] hover:bg-[#1E4A3F] text-white"
              >
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Entrando…</> : 'Entrar'}
              </Button>
            </form>

            {clinicaDatos.demo_autologin && (
              <div className="mt-6">
                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">
                    Acceso rápido demo
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  {cuentasDemo.map((acc) => (
                    <button
                      key={acc.email}
                      type="button"
                      disabled={busy}
                      onClick={() => quickLogin(acc)}
                      data-testid={`demo-login-${acc.label.toLowerCase()}`}
                      className="flex flex-col items-start rounded-lg border border-border px-3 py-2 text-left transition-colors hover:border-[#2E6E5E] hover:bg-[#2E6E5E]/5 disabled:opacity-50"
                    >
                      <span className="text-sm font-semibold text-[#13231E]">Entrar como {acc.label}</span>
                    </button>
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground text-center">
                  Entorno de demostración · sin escribir contraseña
                </p>
              </div>
            )}

            <div className="mt-5 pt-4 border-t border-border">
              <p className="text-xs text-muted-foreground leading-relaxed" data-testid="login-forgot-hint">
                <strong className="text-[#13231E]">¿No recuerdas tu contraseña?</strong>{' '}
                Pide a un administrador que la resetee: <em>Usuarios</em> → botón <span className="inline-block px-1 rounded bg-muted/60">🔑</span>.
                Tras 5 intentos fallidos el sistema bloquea el login durante 15 minutos.
              </p>
              <div className="lg:hidden mt-4">
                <ClinicPilotBadge />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
